import { Injectable, inject } from '@angular/core';
import {Alert} from '../model/alert.model';
import {AlertTypeEnum} from '../model/enums/alert-type.enum';
import {Observable, Subject} from 'rxjs';
import {NavigationStart, Router} from '@angular/router';
import {filter} from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class AlertService {
    private readonly router = inject(Router);


    private readonly subject = new Subject<Alert>();
    private keepAfterRouteChange = false;

    constructor() {
    // clear alert messages on route change unless 'keepAfterRouteChange' flag is true
        const result = this.router.events
            .subscribe({
                next: event => {
                    if (event instanceof NavigationStart) {
                        if (this.keepAfterRouteChange) {
                            // only keep for a single route change
                            this.keepAfterRouteChange = false;
                        } else {
                            // clear alert messages
                            this.clear();
                        }
                    }
                },
                complete: () => {
                    result.unsubscribe();
                }
            });
    }

    // enable subscribing to alerts observable
    onAlert(alertId?: string): Observable<Alert> {
        return this.subject.asObservable().pipe(filter(x => x && x.alertId === alertId));
    }

    // convenience methods
    success(message: string, alertId?: string) {
        this.alert(new Alert({message, alertId, type: AlertTypeEnum.SUCCESS}));
    }

    error(message: string, alertId?: string, phoneNumber?: string) {
        this.alert(new Alert({message, alertId, phoneNumber, type: AlertTypeEnum.ERROR}));
    }

    errorLinkToHome(message: string, alertId?: string) {
        this.alert(new Alert({message, alertId, type: AlertTypeEnum.ERROR, redirectInscription: true}));
    }

    info(message: string, alertId?: string) {
        this.alert(new Alert({message, alertId, type: AlertTypeEnum.INFO}));
    }

    warn(message: string, alertId?: string) {
        this.alert(new Alert({message, alertId, type: AlertTypeEnum.WARNING}));
    }

    // main alert method
    alert(alert: Alert) {
        this.keepAfterRouteChange = alert.keepAfterRouteChange;
        this.subject.next(alert);
    }

    // clear alerts
    clear(alertId?: string) {
        this.subject.next(new Alert({alertId}));
    }
}
